import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useLazyQuery, useMutation } from '@apollo/client';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { getPersonAddressDetails } from '../queries';
import { getInputs } from './content';
import {
  createPersonAddress,
  getPersonAddressLovs,
  updatePersonAddress
} from './queries';
import { LookupToList } from './utils';

import { cloneDeep, isEmpty } from 'lodash';

interface IPersonAddressDrawerProps {
  proposalId?: string;
  personAddressId?: string;
  personId: string;
  open: boolean;
  isProposal?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSubmitSuccess?: (id: string, address: string, personId: string) => void;
}

const PersonAddressDrawer: React.FC<IPersonAddressDrawerProps> = ({
  personAddressId,
  personId,
  open,
  isProposal = false,
  onClose,
  onSuccess,
  onSubmitSuccess,
  proposalId
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const navigate = useNavigate();

  const [personAddressLovsResults] = useLazyQuery(getPersonAddressLovs());
  const [personAddressUpdateAction] = useMutation(updatePersonAddress());
  const [personAddressCreateAction] = useMutation(createPersonAddress());

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [values, setValues] = useState<Record<string, any>>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>([]);

  const [personAddressInfoQuery] = useLazyQuery(getPersonAddressDetails(), {
    fetchPolicy: 'no-cache'
  });

  const loadLovList = async () => {
    const result = await personAddressLovsResults({ fetchPolicy: 'no-cache' });

    return LookupToList(result.data);
  };

  const getEntityInfo = async () => {
    let personAddressEntity: IPersonAddressInfo = {
      personId: '',
      personAddressId: '',
      addressType: '',
      country: '',
      countryId: '',
      zone: '',
      city: '',
      street: '',
      building: '',
      addressDetails: '',
      addressDetailsArabic: '',
      isPrimary: false
    };

    if (personAddressId) {
      const { data } = await personAddressInfoQuery({
        variables: { personAddressId }
      });
      // personAddressInfo
      const result = data?.salesforce?.getPersonAddressDetails;

      if (result) {
        personAddressEntity = {
          personId: result.personId,
          personAddressId: result.id,
          addressType: result.addressType || '',
          country: result.address?.country || '',
          countryId: result.address?.country || '',
          zone: result.address?.zone || '',
          city: result.address?.city || '',
          street: result.address?.street || '',
          building: result.address?.building || '',
          addressDetails: result.address?.addressDetails || '',
          addressDetailsArabic: result.address?.addressDetailsArabic || '',
          isPrimary: result.isPrimary || false
        };
      }
    }

    return personAddressEntity;
  };

  const initialize = async () => {
    try {
      const newValues = {
        addressType: '',
        country: '',
        zone: '',
        city: '',
        street: '',
        building: '',
        addressDetails: '',
        addressDetailsArabic: '',
        isPrimary: false
      };
      const newLovs: Record<string, Record<string, string>> = {};
      const [entityData, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList()
      ]);

      if (lovData) {
        newLovs.addressType = lovData.addressType;
        newLovs.country = lovData.countries;
        newLovs.zone = lovData.zone;
      }

      if (entityData) {
        Object.assign(newValues, {
          addressType: entityData.addressType,
          country: entityData.countryId,
          zone: entityData.zone,
          city: entityData.city,
          street: entityData.street,
          building: entityData.building,
          addressDetails: entityData.addressDetails,
          addressDetailsArabic: entityData.addressDetailsArabic,
          isPrimary: entityData.isPrimary
        });
      }

      setLovs(newLovs);
      setValues(newValues);
      setInputsForm(getInputs(newValues, newLovs, isProposal));
      setBooted(true);
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (vs: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(inputsForm, vs);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const input = {
        personId,
        type: data.addressType,
        country: data.country,
        city: data.city,
        isPrimary: isProposal ? true : data.isPrimary,
        zone: data.zone,
        street: data.street,
        building: data.building,
        addressDetails: data.addressDetails,
        addressDetailsArabic: data.addressDetailsArabic
      };

      const modifyInput = {
        id: personAddressId,
        personId,
        type: data.addressType,
        country: data.country,
        city: data.city,
        isPrimary: !!data.isPrimary,
        zone: data.zone,
        street: data.street,
        building: data.building,
        addressDetails: data.addressDetails,
        addressDetailsArabic: data.addressDetailsArabic
      };

      let res;
      if (personAddressId) {
        res = await personAddressUpdateAction({
          variables: { input: modifyInput },
          errorPolicy: 'all'
        });
      } else {
        res = await personAddressCreateAction({
          variables: { input, proposalID: proposalId },
          errorPolicy: 'all'
        });
      }

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {personAddressId
              ? 'Person Address was modified successfully!'
              : 'Person Address was added successfully!'}
          </ToastSuccessMessage>
        );

        const newAddressId = res?.data?.salesforce?.createPersonAddress || '';

        const fullAddress = [
          input.building,
          input.street,
          lovs.zone?.[input.zone] || input.zone,
          input.city,
          lovs.country?.[input.country] || input.country
        ]
          .filter(Boolean)
          .join(', ');

        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          if (onSubmitSuccess)
            onSubmitSuccess(newAddressId, fullAddress, personId);
          if (onSuccess) onSuccess();
          onClose();
          if (!personId) {
            navigate(`/salesforce/persons/${personId}`);
          }
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const onChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    newValues: Record<string, any>
  ) => {
    const newLovs = cloneDeep(lovs);

    if (fieldName === 'country') {
      if (value === 'LB') {
        // newValues.zone = lovs.zone;
        // setLovs(newLovs);
      } else {
        newValues.zone = null;
      }
    }

    setValues(newValues);
    setInputsForm(getInputs(newValues, newLovs, isProposal));
  };
  if (!booted) return <></>;

  return (
    <DynamicFormDrawer
      title={
        personAddressId
          ? 'Edit Address'
          : isProposal
            ? 'Add Person Primary Address'
            : 'New Person Address'
      }
      onClose={onClose}
      isOpen={open}
      sections={inputsForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
    />
  );
};

export default PersonAddressDrawer;
